import getDefaultOpts from "@/store/utils/getDefaultOpts.js";
import { debounce } from "lodash";
import { patchEntryMutation } from "@/store/api/actions/patchEntry.js";
import { patchCategoriesMutation } from "@/store/api/actions/patchCategories.js";
import { patchSiteMutation } from "@/store/api/actions/patchSite.js";
import { patchCategoryMutation } from "@/store/api/actions/patchCategory.js";

async function patchData(
  dispatch,
  page,
  group,
  categoryID,
  entryID,
  patch,
  opts
) {
  if (group === "page") {
    switch (page) {
      case "Home":
        // the home doc is deprecated and we save the home data in the site doc
        return dispatch("patchSite", {
          patch,
          opts,
        });
      case "Categories":
        return dispatch("patchCategories", {
          patch,
          opts,
        });
      case "SubCategories":
      case "Category":
        return dispatch("patchCategory", {
          path: `/category/${categoryID}`,
          patch,
          opts,
        });
      case "Person":
        return dispatch("patchEntry", {
          categoryID,
          path: `/entry/${entryID}`,
          patch,
          opts,
        });
      default:
        return Promise.reject(new Error(`Invalid page ${page}.`));
    }
  } else {
    return dispatch("patchSite", {
      patch,
      opts,
    });
  }
}

const debouncedPatchMap = new Map();

function getDebouncedPatchData(debounceKey) {
  if (!debouncedPatchMap.has(debounceKey)) {
    // Create a new debounced function for each unique key
    debouncedPatchMap.set(debounceKey, debounce(patchData, 2000));
  }
  return debouncedPatchMap.get(debounceKey);
}

/*
In order to make the CMS more responsive we write store data before it has been sent to the API.
 */
function preActionMutation(module, dataKey, value, categoryID, entryID) {
  const page = dataKey[0];
  const group = dataKey[1];
  const type = dataKey[2];
  const name = dataKey[3];
  const patch = {
    [name]: value,
  };
  module.dispatch(
    "setStoreVar",
    {
      page,
      group,
      type,
      name,
      value,
    },
    { root: true }
  );
  if (group === "page") {
    switch (page) {
      case "Home":
        return patchSiteMutation(module, patch);
      case "Categories":
        return patchCategoriesMutation(module, patch);
      case "SubCategories":
      case "Category":
        return patchCategoryMutation(module, categoryID, patch);
      case "Person":
        return patchEntryMutation(
          module,
          categoryID,
          `/entry/${entryID}`,
          patch
        );
      default:
        return Promise.reject(new Error(`Invalid page ${page}.`));
    }
  } else {
    return patchSiteMutation(module, patch);
  }
}

/*
This action will write site data to the API
as well as update it in Vuex.

dataKey is an array e.g. ["Person", "page", "media", "media"]
value is the new value e.g. "myval"
 */
async function saveSiteData(
  module,
  { dataKey, value, categoryID, entryID, opts = {} }
) {
  if (dataKey[3] === "homeButtons") {
    throw new Error("Home buttons should use action saveHomeButtonData.");
  }
  const { dispatch } = module;
  const config = Object.assign(getDefaultOpts(), opts);

  preActionMutation(module, dataKey, value, categoryID, entryID);

  const patch = {
    [dataKey[3]]: value,
  };

  // Use a separate debounced function per unique field by creating a unique key
  if (opts.debounce) {
    const debounceKey = dataKey.join("_");
    return getDebouncedPatchData(debounceKey)(
      dispatch,
      dataKey[0],
      dataKey[1],
      categoryID,
      entryID,
      patch,
      config
    );
  }
  return patchData(
    dispatch,
    dataKey[0],
    dataKey[1],
    categoryID,
    entryID,
    patch,
    config
  );
}

export { saveSiteData };
